import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Download, FolderOpen, X, File, FileText, FileSpreadsheet, FileImage, FileArchive,
  FileVideo, FileAudio, Presentation,
} from 'lucide-react';

// Panel de Descargas de la app de escritorio. Fuera del navegador no hay barra
// ni animacion de descarga: la app avisa aqui lo que baja, con su progreso, y
// deja abrir el archivo o su carpeta. Al empezar una descarga, el archivo vuela
// desde donde se hizo clic hasta el boton y el panel se abre al aterrizar; se
// cierra solo al terminar si nadie lo toco.
const api = typeof window !== 'undefined' ? window.electronAPI?.descargas : null;
export const hayDescargas = Boolean(api);

const VISTO_KEY = 'descargas_visto_hasta';
const CIERRE_SOLO_MS = 5000;
const ANCHO = 352;
const VUELO_S = 0.75;
// Un clic cuenta como origen del vuelo si la descarga arranca poco despues
// (algunos archivos se arman en el servidor y tardan unos segundos)
const CLIC_VALIDO_MS = 20000;
// Una descarga es "nueva" si aparece con menos de esto: las muy cortas pueden
// llegar ya completas, sin pasar por "en progreso"
const RECIENTE_MS = 30000;

const movimientoReducido = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const TIPOS = [
  { ext: ['pdf'], Icono: FileText, color: 'text-red-600 bg-red-50' },
  { ext: ['xls', 'xlsx', 'xlsm', 'csv', 'ods'], Icono: FileSpreadsheet, color: 'text-emerald-600 bg-emerald-50' },
  { ext: ['doc', 'docx', 'odt', 'rtf', 'txt'], Icono: FileText, color: 'text-blue-600 bg-blue-50' },
  { ext: ['ppt', 'pptx', 'odp'], Icono: Presentation, color: 'text-orange-600 bg-orange-50' },
  { ext: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'heic', 'bmp'], Icono: FileImage, color: 'text-violet-600 bg-violet-50' },
  { ext: ['zip', 'rar', '7z'], Icono: FileArchive, color: 'text-amber-600 bg-amber-50' },
  { ext: ['mp4', 'mov', 'avi', 'webm', 'mkv'], Icono: FileVideo, color: 'text-pink-600 bg-pink-50' },
  { ext: ['mp3', 'wav', 'm4a', 'ogg'], Icono: FileAudio, color: 'text-sky-600 bg-sky-50' },
];
const tipoDe = (nombre) => {
  const ext = (nombre.split('.').pop() || '').toLowerCase();
  return TIPOS.find((t) => t.ext.includes(ext)) || { Icono: File, color: 'text-slate-500 bg-slate-100' };
};

const tamano = (bytes) => {
  if (!bytes) return '';
  const unidades = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < unidades.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toLocaleString('es', { maximumFractionDigits: n < 10 && i > 0 ? 1 : 0 })} ${unidades[i]}`;
};

const relativo = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });
const haceCuanto = (ms) => {
  const seg = Math.round((Date.now() - ms) / 1000);
  if (seg < 45) return 'hace un momento';
  if (seg < 3600) return relativo.format(-Math.round(seg / 60), 'minute');
  if (seg < 86400) return relativo.format(-Math.round(seg / 3600), 'hour');
  return new Date(ms).toLocaleDateString('es', { day: 'numeric', month: 'short' });
};

const leerVisto = () => {
  try {
    return Number(localStorage.getItem(VISTO_KEY)) || 0;
  } catch {
    return 0;
  }
};
const guardarVisto = (ms) => {
  try {
    localStorage.setItem(VISTO_KEY, String(ms));
  } catch {
    // Sin almacenamiento: el punto vuelve a salir al recargar, no es grave
  }
};

// Anillo de progreso alrededor del boton (girando si no se sabe el tamaño)
const Anillo = ({ fraccion }) => {
  const r = 16;
  const largo = 2 * Math.PI * r;
  const indeterminado = fraccion == null;
  return (
    <svg viewBox="0 0 36 36" className={`absolute inset-0 w-full h-full -rotate-90 ${indeterminado ? 'animate-spin' : ''}`} aria-hidden="true">
      <circle cx="18" cy="18" r={r} fill="none" stroke="currentColor" strokeOpacity="0.15" strokeWidth="2" />
      <circle
        cx="18" cy="18" r={r} fill="none" stroke="#67e8f9" strokeWidth="2" strokeLinecap="round"
        strokeDasharray={largo}
        strokeDashoffset={largo * (1 - (indeterminado ? 0.25 : fraccion))}
        style={{ transition: 'stroke-dashoffset 300ms ease-out' }}
      />
    </svg>
  );
};

// El archivo que vuela hasta el boton: sube en arco y cae acelerando
const Vuelo = ({ v, alLlegar }) => {
  const { Icono, color } = tipoDe(v.nombre);
  const cima = { x: (v.origen.x + v.fin.x) / 2, y: Math.min(v.origen.y, v.fin.y) - 140 };
  return (
    <motion.div
      aria-hidden="true"
      className={`fixed left-0 top-0 z-[9999] -ml-5 -mt-5 w-10 h-10 rounded-xl flex items-center justify-center pointer-events-none shadow-xl shadow-black/30 ring-1 ring-white/70 ${color}`}
      initial={{ x: v.origen.x, y: v.origen.y, scale: 0.4, opacity: 0, rotate: -14 }}
      animate={{
        x: [v.origen.x, cima.x, v.fin.x],
        y: [v.origen.y, cima.y, v.fin.y],
        scale: [0.4, 1.15, 0.35],
        opacity: [0, 1, 0.85],
        rotate: [-14, 8, 0],
      }}
      transition={{ duration: VUELO_S, delay: v.retraso, times: [0, 0.45, 1], ease: ['easeOut', 'easeIn'] }}
      onAnimationComplete={() => alLlegar(v.id)}
    >
      <Icono size={20} />
    </motion.div>
  );
};

const Fila = ({ d }) => {
  const { Icono, color } = tipoDe(d.nombre);
  const enProgreso = d.estado === 'progreso';
  const abrible = d.estado === 'completa' && d.existe;
  const fraccion = d.total > 0 ? Math.min(1, d.recibidos / d.total) : null;

  let detalle;
  if (enProgreso) {
    detalle = d.total > 0 ? `${tamano(d.recibidos) || '0 B'} de ${tamano(d.total)}` : tamano(d.recibidos) || 'Empezando…';
  } else if (d.estado === 'fallida') {
    detalle = <span className="text-red-600">No se pudo descargar</span>;
  } else if (!d.existe) {
    detalle = 'Se movió o se borró';
  } else {
    detalle = [tamano(d.recibidos || d.total), haceCuanto(d.fin || d.inicio)].filter(Boolean).join(' · ');
  }

  const contenido = (
    <>
      <span className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center ${abrible || enProgreso ? color : 'text-slate-400 bg-slate-100'}`}>
        <Icono size={18} aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block truncate text-sm font-medium ${abrible || enProgreso ? 'text-slate-800' : 'text-slate-400'}`} title={d.nombre}>
          {d.nombre}
        </span>
        {enProgreso && (
          <span className="mt-1.5 block h-1 rounded-full bg-slate-100 overflow-hidden">
            <span
              className={`block h-full rounded-full bg-cyan-500 ${fraccion == null ? 'w-1/3 animate-pulse' : ''}`}
              style={fraccion == null ? undefined : { width: `${Math.max(4, fraccion * 100)}%`, transition: 'width 300ms ease-out' }}
            />
          </span>
        )}
        <span className="mt-0.5 block truncate text-xs text-slate-500">{detalle}</span>
      </span>
    </>
  );

  return (
    <li className="group relative flex items-center rounded-lg hover:bg-slate-50 focus-within:bg-slate-50">
      {abrible ? (
        <button
          type="button"
          onClick={() => api.abrir(d.id)}
          title="Abrir"
          className="flex items-center gap-3 min-w-0 flex-1 px-2 py-2 pr-20 text-left rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/60"
        >
          {contenido}
        </button>
      ) : (
        <div className="flex items-center gap-3 min-w-0 flex-1 px-2 py-2 pr-20">{contenido}</div>
      )}
      <span className="absolute right-2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
        {abrible && (
          <button
            type="button"
            onClick={() => api.mostrarEnCarpeta(d.id)}
            aria-label={`Mostrar ${d.nombre} en la carpeta`}
            title="Mostrar en la carpeta"
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-200/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/60"
          >
            <FolderOpen size={16} />
          </button>
        )}
        <button
          type="button"
          onClick={() => (enProgreso ? api.cancelar(d.id) : api.quitar(d.id))}
          aria-label={enProgreso ? `Cancelar ${d.nombre}` : `Quitar ${d.nombre} de la lista`}
          title={enProgreso ? 'Cancelar' : 'Quitar de la lista'}
          className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-200/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/60"
        >
          <X size={16} />
        </button>
      </span>
    </li>
  );
};

const DescargasEscritorio = () => {
  const [lista, setLista] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [visto, setVisto] = useState(leerVisto);
  const [pos, setPos] = useState(null);
  const [, setReloj] = useState(0);
  const boton = useRef(null);
  const panel = useRef(null);
  const conocidas = useRef(null);
  const abiertoSolo = useRef(false);
  const encima = useRef(false);
  const ultimoClic = useRef(null);
  const [vuelos, setVuelos] = useState([]);
  const [llegadas, setLlegadas] = useState(0);

  const abrirSolo = useCallback(() => {
    setAbierto((ya) => {
      if (!ya) abiertoSolo.current = true;
      return true;
    });
  }, []);

  // Donde fue el ultimo clic: de ahi sale volando el archivo
  useEffect(() => {
    if (!api) return undefined;
    const anotar = (e) => {
      ultimoClic.current = { x: e.clientX, y: e.clientY, t: Date.now() };
    };
    window.addEventListener('pointerdown', anotar, { capture: true, passive: true });
    return () => window.removeEventListener('pointerdown', anotar, { capture: true });
  }, []);

  // Lista inicial y cambios que avisa la app. Una descarga nueva vuela al boton
  // (o, sin animaciones, abre el panel de una vez).
  useEffect(() => {
    if (!api) return undefined;
    const lanzar = (nuevas) => {
      const destino = boton.current?.getBoundingClientRect();
      if (!destino || movimientoReducido()) {
        abrirSolo();
        return;
      }
      const clic = ultimoClic.current;
      const origen = clic && Date.now() - clic.t < CLIC_VALIDO_MS
        ? clic
        : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
      ultimoClic.current = null;
      const fin = { x: destino.left + destino.width / 2, y: destino.top + destino.height / 2 };
      setVuelos((v) => [
        ...v,
        ...nuevas.map((d, i) => ({ id: d.id, nombre: d.nombre, origen, fin, retraso: i * 0.12 })),
      ]);
    };
    const recibir = (nueva) => {
      setLista(nueva);
      if (conocidas.current) {
        const nuevas = nueva.filter((d) => !conocidas.current.has(d.id) && Date.now() - d.inicio < RECIENTE_MS);
        if (nuevas.length) lanzar(nuevas);
      }
      conocidas.current = new Set(nueva.map((d) => d.id));
    };
    api.lista().then(recibir);
    return api.alCambiar(recibir);
  }, [abrirSolo]);

  // Al aterrizar: el boton rebota y se abre el panel
  const aterrizo = useCallback((id) => {
    setVuelos((v) => v.filter((x) => x.id !== id));
    setLlegadas((n) => n + 1);
    abrirSolo();
  }, [abrirSolo]);

  const activas = lista.filter((d) => d.estado === 'progreso');
  const recibido = activas.reduce((s, d) => s + d.recibidos, 0);
  const total = activas.reduce((s, d) => s + d.total, 0);
  const fraccion = activas.length && activas.every((d) => d.total > 0) ? recibido / total : null;
  const sinVer = lista.some((d) => d.estado === 'completa' && (d.fin || 0) > visto);
  const hayTerminadas = lista.some((d) => d.estado !== 'progreso');

  const cerrar = useCallback(() => {
    setAbierto(false);
    abiertoSolo.current = false;
  }, []);

  // Si se abrio solo, se cierra solo al terminar todo (salvo que el puntero este encima)
  useEffect(() => {
    if (!abierto || !abiertoSolo.current || activas.length) return undefined;
    const id = setTimeout(() => {
      if (!encima.current) cerrar();
    }, CIERRE_SOLO_MS);
    return () => clearTimeout(id);
  }, [abierto, activas.length, cerrar]);

  // Abierto = visto
  useEffect(() => {
    if (!abierto) return;
    const ahora = Date.now();
    setVisto(ahora);
    guardarVisto(ahora);
  }, [abierto, lista]);

  // Posicion: a la derecha del sidebar, con el borde de abajo alineado al boton
  useLayoutEffect(() => {
    if (!abierto) return undefined;
    const ubicar = () => {
      const r = boton.current?.getBoundingClientRect();
      if (!r) return;
      const left = r.right + 12;
      setPos({ left, bottom: Math.max(12, window.innerHeight - r.bottom), width: Math.min(ANCHO, window.innerWidth - left - 12) });
    };
    ubicar();
    window.addEventListener('resize', ubicar);
    return () => window.removeEventListener('resize', ubicar);
  }, [abierto]);

  // Cerrar al hacer clic afuera o con Escape; refrescar los "hace X min"
  useEffect(() => {
    if (!abierto) return undefined;
    const afuera = (e) => {
      if (panel.current?.contains(e.target) || boton.current?.contains(e.target)) return;
      cerrar();
    };
    const tecla = (e) => {
      if (e.key === 'Escape') {
        cerrar();
        boton.current?.focus();
      }
    };
    const reloj = setInterval(() => setReloj((n) => n + 1), 30000);
    document.addEventListener('mousedown', afuera);
    document.addEventListener('keydown', tecla);
    return () => {
      clearInterval(reloj);
      document.removeEventListener('mousedown', afuera);
      document.removeEventListener('keydown', tecla);
    };
  }, [abierto, cerrar]);

  if (!api) return null;

  return (
    <>
      <button
        ref={boton}
        type="button"
        onClick={() => {
          abiertoSolo.current = false;
          setAbierto((a) => !a);
        }}
        aria-label={activas.length ? `Descargas: ${activas.length} en curso` : 'Descargas'}
        aria-expanded={abierto}
        aria-controls="panel-descargas"
        title="Descargas"
        className={`relative w-9 h-9 flex items-center justify-center rounded-lg hover:text-white hover:bg-white/5 active:scale-90 transition-[color,background-color,transform] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 ${activas.length || abierto ? 'text-white' : 'text-white/40'}`}
      >
        {activas.length > 0 && <Anillo fraccion={fraccion} />}
        {llegadas > 0 && (
          <motion.span
            key={`onda-${llegadas}`}
            aria-hidden="true"
            className="absolute inset-0 rounded-lg ring-2 ring-cyan-300"
            initial={{ opacity: 0.9, scale: 0.7 }}
            animate={{ opacity: 0, scale: 1.7 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
          />
        )}
        <motion.span
          key={`rebote-${llegadas}`}
          className="flex"
          initial={llegadas ? { scale: 1 } : false}
          animate={llegadas ? { scale: [1, 1.35, 0.9, 1] } : { scale: 1 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
        >
          <motion.span
            className="flex"
            animate={activas.length ? { y: [-2, 2, -2] } : { y: 0 }}
            transition={activas.length ? { duration: 1.1, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.2 }}
          >
            <Download size={activas.length ? 16 : 18} />
          </motion.span>
        </motion.span>
        {sinVer && !abierto && !activas.length && (
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-cyan-300 shadow-[0_0_6px_rgba(103,232,249,0.8)]" aria-hidden="true" />
        )}
      </button>

      {vuelos.length > 0 && createPortal(
        vuelos.map((v) => <Vuelo key={v.id} v={v} alLlegar={aterrizo} />),
        document.body,
      )}

      {createPortal(
        <AnimatePresence>
          {abierto && pos && (
            <motion.div
              ref={panel}
              id="panel-descargas"
              role="dialog"
              aria-label="Descargas"
              initial={{ opacity: 0, x: -8, scale: 0.98 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -8, scale: 0.98 }}
              transition={{ duration: 0.16, ease: 'easeOut' }}
              onMouseEnter={() => { encima.current = true; }}
              onMouseLeave={() => { encima.current = false; }}
              onMouseDown={() => { abiertoSolo.current = false; }}
              style={{ left: pos.left, bottom: pos.bottom, width: pos.width, transformOrigin: 'bottom left' }}
              className="fixed z-[9998] rounded-xl bg-white shadow-2xl shadow-black/25 ring-1 ring-slate-900/10 overflow-hidden"
            >
              <div className="flex items-center justify-between gap-2 pl-4 pr-2 py-2.5 border-b border-slate-100">
                <h2 className="text-sm font-semibold text-slate-800">Descargas</h2>
                <div className="flex items-center gap-1">
                  {hayTerminadas && (
                    <button
                      type="button"
                      onClick={() => api.limpiar()}
                      className="px-2 py-1 rounded-md text-xs font-medium text-slate-500 hover:text-slate-800 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/60"
                    >
                      Limpiar lista
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => api.abrirCarpeta()}
                    aria-label="Abrir la carpeta de descargas"
                    title="Abrir la carpeta de descargas"
                    className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/60"
                  >
                    <FolderOpen size={16} />
                  </button>
                </div>
              </div>
              {lista.length ? (
                <ul className="max-h-[min(380px,60vh)] overflow-y-auto p-1.5 space-y-0.5">
                  {lista.map((d) => <Fila key={d.id} d={d} />)}
                </ul>
              ) : (
                <p className="px-6 py-8 text-center text-sm text-slate-500">
                  Aquí aparece lo que descargues desde Centro SURA.
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
};

export default DescargasEscritorio;
